import { ComboBox, ComboBoxChangeEvent, ComboBoxFilterChangeEvent, DropDownList, DropDownListChangeEvent, MultiColumnComboBox, MultiColumnComboBoxColumn, MultiColumnComboBoxHandle } from '@progress/kendo-react-dropdowns';
import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import React, { ChangeEvent, FunctionComponent, useEffect, useMemo, useRef } from 'react'
import { Api } from '../../../redux/api/BaseApi';
import {
  Label,
  Error,
  Hint,
} from "@progress/kendo-react-labels";
import { UserApi } from '../../../redux/api/UserApi';
import { ClientApi } from '../../../redux/api/ClientApi';
import { FilterDescriptor, filterBy } from "@progress/kendo-data-query";
import { IClient } from '../../../types/Client/IClient';
import { IUser } from '../../../types/User/IUser';
import FilterButton from '../../ClientType/FilterButton';
import BulkLogButton from '../FormControls/BulkLogButton';
import UserDetailsModal from '../../User/UserDetailsModal';

const UserFynd: FunctionComponent<FieldRenderProps> = props => {
  const {
    validationMessage,
    label,
    id,
    disabled,
    hint,
    optional,
    endUserId,
    hideOptions
  } = props;
  const { data: profile } = Api.useGetUserProfileQuery();
  const requiredMessage = `${profile?.Phrases.EndUser} is required`
  const { data: columns } = Api.useGetUserFyndConfigQuery();
  const [postSearch, { isLoading, data }] = Api.usePostUserFyndMutation();
  const { data: users, isFetching: usersLoading } = UserApi.useGetUsersQuery({ companyId: props.companyId })
  const { data: clients } = ClientApi.useGetMyClientsQuery(undefined);
  const { data: classification } = UserApi.useGetUserClassificationQuery();

  const niceColumns: MultiColumnComboBoxColumn[] = useMemo(() => {
    return columns?.map<MultiColumnComboBoxColumn>(c => { return { field: c.Display_Name, header: c.Display_Name, width: '' } as MultiColumnComboBoxColumn })
  }, [columns]);

  const [clientOpen, setClientOpen] = React.useState<boolean>(false);
  const [userOpen, setUserOpen] = React.useState<boolean>(false);
  const [filter, setFilter] = React.useState<FilterDescriptor>();
  const [userFilter, setUserFilter] = React.useState<FilterDescriptor>();
  const filterChange = (event) => {
    setFilter(event.filter);
  };
  const userFilterChange = (event) => {
    setUserFilter(event.filter);
  };
  const filteredUsers = (userFilter ? filterBy(users.slice(), userFilter) : users)?.map(u => { return { ...u, Address_Line_1: u.Address_Line_1 || '', Bus_Phone: u.Bus_Phone || '', Title: u.Title || '', Custom1: u.Custom1 || '', Custom2: u.Custom2 || '', Custom3: u.Custom3 || '', Custom4: u.Custom4 || '', Custom5: u.Custom5 || '', Custom6: u.Custom6 || '', Company_Abbrev: u.Company_Abbrev || '' } });
  const filteredClients = filter ? filterBy(clients.slice(), filter) : clients
  const selectedClient = filteredClients?.find(c => c.ID === props.companyId) || {} as IClient;
  const selectedUser = users?.find(c => c.ID === endUserId) || {} as IUser;
  const sortedUsers = classification?.Secondary_Sort ? filteredUsers?.sort((a, b) => classification?.Secondary_Sort_Direction === 'Desc' ? b[classification.Field_Name].toString().localeCompare(a[classification.Field_Name]) : a[classification.Field_Name].toString().localeCompare(b[classification.Field_Name])) : filteredUsers;
  const combo = useRef<MultiColumnComboBoxHandle>();

  //Auto focus the combo box when the component is loaded
  useEffect(() => {
    if (combo.current && !endUserId) {
      combo.current.focus();
    }
  }, [combo, endUserId]);

  //Select the first client if there is only one
  useEffect(() => {
    if (clients?.length === 1)
      props?.onClientChange({ value: clients[0] })
  }, [clients]);

  //Event handlers
  const endUserFilterHandler = (e: ComboBoxFilterChangeEvent) => {
    //console.log('endUserChangeHandler', e.filter);
    if (e.filter.value.length > 2)
      postSearch(e.filter.value);
  }
  const endUserChangeHandler = (e: ComboBoxChangeEvent) => {
    console.log('endUserChangeHandler', e.value);
    props?.onChange(e.value);
  }

  const endUserCreated = (newEndUser: IUser) => {

    console.log('endUserCreated', newEndUser);

    props?.onChange({ target: null, value: newEndUser.ID, ...newEndUser });
  }

  function endUserChanged(e: ComboBoxChangeEvent): void {
    console.log('endUserChanged', e.value, userOpen)

    setUserOpen(false);
    setUserFilter(null);
    props?.onChange({ ...e.value, Contact_Id: e.value.ID });

  }

  function clientChanged(e: ComboBoxChangeEvent): void {
    setClientOpen(false);

    props?.onClientChange(e);

    setUserOpen(true);
    setFilter(null);
  }


  const itemRender = (li, props) => {
    const children = columns?.map((col, i) => {
      return (
        <span className="k-table-td user-fynd-item" key={i}>
          <div dangerouslySetInnerHTML={{ __html: props.dataItem[col.Display_Name] }} />
        </span>
      );
    });
    return React.cloneElement(li, { ...li.props }, children);
  };
  const endUserColumns = classification?.Field_Name ? [
    {
      field: "FullName",
      header: "Name",
      width: "40%",
    },
    {
      field: classification?.Field_Name,
      header: classification?.Display_Name,
      width: null,
    }
  ] : [
    {
      field: "FullName",
      header: "Name",
      width: "50%",
    }
  ]

  const wrapper = useRef(null);
  const showValidationMessage = validationMessage;
  const isValid = !validationMessage
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";

  const filterButton = !hideOptions && profile?.Features.Show_Client_Type_Filter ? <div className='inline-block pl-1'>
    <FilterButton onClick={props.filterClicked} clientTypePhrase={profile?.Phrases.Client} />
  </div> : <></>

  const bulkLog = !hideOptions && profile?.Features.Bulk_Call_Logging && <div className='inline-block pl-1'>
    <BulkLogButton badgeCount={props.bulkContactIds?.length} onClick={props.toggleBulkLogging}></BulkLogButton>
  </div>

  const addEditUser = !hideOptions && profile?.Features.Add_Edit_EndUSer_From_LogACall && <div className='inline-block pl-1'>
    <UserDetailsModal companyId={props.companyId} contactId={endUserId} userCreated={endUserCreated}></UserDetailsModal>
  </div>


  return (
    profile?.Features.UserFynd ?
      <FieldWrapper>
        <Label
          editorId={id}
          editorValid={isValid}
          editorDisabled={disabled}
          optional={optional}
        >
          {label} {showValidationMessage && (<Error id={errorId}>{validationMessage}</Error>)}
        </Label>

        <div className={"k-form-field-wrap1  inline-block w-10/12"} ref={wrapper} >
          <MultiColumnComboBox ref={combo} valid={isValid} size='small' popupSettings={{ className: 'z500 user-fynd-results', popupClass: 'z500', appendTo: wrapper.current }} className='az500' itemRender={itemRender} placeholder='Type 3 characters to search...' value={props.display} columns={niceColumns} onChange={endUserChangeHandler} onFilterChange={endUserFilterHandler} data={data} filterable={true} loading={isLoading} required={props.isRequired} validationMessage={requiredMessage} />
        </div>
        {addEditUser}
        {filterButton}
        {bulkLog}
        {showHint && <Hint id={hintId}>{hint}</Hint>}
      </FieldWrapper>
      : <>
        <FieldWrapper>
          <Label
            editorId={id}
            editorValid={isValid}
            editorDisabled={disabled}
            optional={optional}
          >
            {profile?.Phrases.Client} {showValidationMessage && !selectedClient?.ID && (<Error id={errorId}>{validationMessage}</Error>)}
          </Label>
          <div className="k-form-field-wrap1 inline-block w-10/12">
            <ComboBox opened={clientOpen} onOpen={e => setClientOpen(true)} onBlur={e => setClientOpen(false)} onFocus={e => setClientOpen(true)} suggest={true} filterable={true} placeholder="Type to search..." popupSettings={{ className: "z500" }} onFilterChange={filterChange} onChange={clientChanged} value={selectedClient} data={filteredClients} dataItemKey="ID" textField="Company_Name" />
          </div>
          {filterButton}
        </FieldWrapper> <FieldWrapper>
          <Label
            editorId={id}
            editorValid={isValid}
            editorDisabled={disabled}
            optional={optional}
          >
            {label} {showValidationMessage && (<Error id={errorId}>{validationMessage}</Error>)}
          </Label>
          <div className="k-form-field-wrap1  inline-block w-10/12">
            {/* selected:{selectedUser?.FullName} */}
            <MultiColumnComboBox columns={endUserColumns} opened={userOpen} onOpen={e => setUserOpen(true)} onBlur={e => setUserOpen(false)} onFocus={e => setUserOpen(true)} suggest={true} filterable={true} loading={usersLoading} placeholder={selectedClient ? "Select" : `Select a ${profile.Phrases.Client}`} popupSettings={{ className: "z500" }} onFilterChange={userFilterChange} onChange={endUserChanged} value={selectedUser} data={sortedUsers} textField="FullName" dataItemKey='ID' />
          </div>
          {addEditUser}
          {bulkLog}
        </FieldWrapper></>
  )
}

export default UserFynd;